import { useMemo, useState } from 'react';
import { AlertTriangle, Ban, Bug, CheckCircle2, Clock, ExternalLink, Eye, Info, RotateCcw, Siren, Wrench } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { formatTimeAgo, type SentryError } from '@/types/performance';

type IssueActionStatus = 'resolved' | 'ignored' | 'unresolved' | 'marked_monitoring';
type KanbanColumnKey = 'needs_fix' | 'monitoring' | 'ready_to_close' | 'done';

interface ErrorsListSentryProps {
  errors: SentryError[];
  isLoading: boolean;
  isUpdating?: boolean;
  onAction?: (payload: {
    issueIds: string[];
    status: IssueActionStatus;
    issueTitles: Record<string, string>;
  }) => Promise<unknown>;
}

const COLUMN_CONFIG: Record<KanbanColumnKey, { title: string; description: string; border: string; bg: string }> = {
  needs_fix: {
    title: 'Precisa corrigir',
    description: 'Teve evento recente e ainda exige ação do time.',
    border: 'border-red-200',
    bg: 'bg-red-50/70',
  },
  monitoring: {
    title: 'Monitorando',
    description: 'Corrigida ou estável, mas ainda dentro da janela de observação.',
    border: 'border-amber-200',
    bg: 'bg-amber-50/70',
  },
  ready_to_close: {
    title: 'Pronta para fechar',
    description: 'Sem recorrência há 24h. Pode resolver no Sentry.',
    border: 'border-emerald-200',
    bg: 'bg-emerald-50/70',
  },
  done: {
    title: 'Fechada / Ignorada',
    description: 'Histórico operacional já tratado.',
    border: 'border-slate-200',
    bg: 'bg-slate-50/70',
  },
};

function getLevelIcon(level: string) {
  switch (level) {
    case 'error':
      return <Bug className="h-4 w-4 text-red-500" />;
    case 'warning':
      return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
    default:
      return <Info className="h-4 w-4 text-blue-500" />;
  }
}

function getLevelBadge(level: string) {
  switch (level) {
    case 'error':
      return <Badge variant="destructive" className="text-xs">Erro</Badge>;
    case 'warning':
      return <Badge variant="secondary" className="text-xs bg-yellow-500/10 text-yellow-700">Aviso</Badge>;
    default:
      return <Badge variant="outline" className="text-xs">Info</Badge>;
  }
}

function getSentryStatusBadge(error: SentryError) {
  const status = error.sentryStatus || error.status;
  if (status === 'resolved') return <Badge variant="secondary" className="text-xs bg-green-500/10 text-green-700">Sentry: Resolvida</Badge>;
  if (status === 'ignored') return <Badge variant="outline" className="text-xs">Sentry: Ignorada</Badge>;
  return <Badge variant="outline" className="text-xs border-orange-200 text-orange-700">Sentry: Aberta</Badge>;
}

function getOperationalMeta(error: SentryError) {
  switch (error.operationalStatus) {
    case 'resolved':
      return {
        column: 'done' as KanbanColumnKey,
        badge: <Badge variant="secondary" className="text-xs bg-green-500/10 text-green-700">Divus: Fechada</Badge>,
        nextStep: 'Issue já resolvida oficialmente no Sentry.',
      };
    case 'ignored':
      return {
        column: 'done' as KanbanColumnKey,
        badge: <Badge variant="outline" className="text-xs">Divus: Ignorada</Badge>,
        nextStep: 'Issue ignorada no Sentry. Reabra se voltar a exigir ação.',
      };
    case 'ready_to_close':
      return {
        column: 'ready_to_close' as KanbanColumnKey,
        badge: <Badge variant="secondary" className="text-xs bg-emerald-500/10 text-emerald-700">Divus: Pronta para fechar</Badge>,
        nextStep: 'Sem recorrência há 24h. Resolver no Sentry agora.',
      };
    case 'monitoring':
      return {
        column: 'monitoring' as KanbanColumnKey,
        badge: <Badge variant="secondary" className="text-xs bg-amber-500/10 text-amber-700">Divus: Monitorando</Badge>,
        nextStep: 'Acompanhar a janela de observação. Se ficar 24h sem evento, fechar.',
      };
    default:
      return {
        column: 'needs_fix' as KanbanColumnKey,
        badge: <Badge variant="outline" className="text-xs border-red-200 text-red-700">Divus: Precisa corrigir</Badge>,
        nextStep: 'Precisa correção do time antes de entrar em monitoramento.',
      };
  }
}

function buildIssueTitles(issueIds: string[], errors: SentryError[]) {
  const map = new Map(errors.map((error) => [error.id, error.title]));
  return issueIds.reduce<Record<string, string>>((acc, issueId) => {
    const title = map.get(issueId);
    if (title) acc[issueId] = title;
    return acc;
  }, {});
}

function ErrorHistoryDialog({ error, open, onOpenChange }: { error: SentryError | null; open: boolean; onOpenChange: (open: boolean) => void }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Historico de acao</DialogTitle>
        </DialogHeader>

        {error ? (
          <div className="space-y-4">
            <div className="rounded-lg border bg-muted/30 p-4">
              <p className="font-medium">{error.title}</p>
              <p className="mt-1 text-xs font-mono text-muted-foreground">{error.culprit}</p>
            </div>

            <ScrollArea className="max-h-[60vh] pr-4">
              <div className="space-y-3">
                {(error.actionHistory || []).length === 0 ? (
                  <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                    Nenhuma acao registrada ainda para esta issue.
                  </div>
                ) : (
                  error.actionHistory?.map((entry) => (
                    <div key={entry.id} className="rounded-lg border p-4">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline" className="text-xs uppercase">{entry.action}</Badge>
                        <Badge variant={entry.resultStatus === 'success' ? 'secondary' : 'destructive'} className="text-xs">
                          {entry.resultStatus === 'success' ? 'Sucesso' : 'Falha'}
                        </Badge>
                        <span className="text-xs text-muted-foreground">{formatTimeAgo(entry.createdAt)}</span>
                      </div>
                      <p className="mt-2 text-sm text-muted-foreground">
                        {entry.actorNameSnapshot || 'Sistema'}
                      </p>
                      {entry.errorMessage ? (
                        <p className="mt-2 rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">
                          {entry.errorMessage}
                        </p>
                      ) : null}
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export function ErrorsListSentry({
  errors,
  isLoading,
  isUpdating = false,
  onAction,
}: ErrorsListSentryProps) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [historyIssue, setHistoryIssue] = useState<SentryError | null>(null);

  const grouped = useMemo(() => {
    return errors.reduce<Record<KanbanColumnKey, SentryError[]>>(
      (acc, error) => {
        acc[getOperationalMeta(error).column].push(error);
        return acc;
      },
      { needs_fix: [], monitoring: [], ready_to_close: [], done: [] },
    );
  }, [errors]);

  const selectedErrors = useMemo(
    () => errors.filter((error) => selectedIds.includes(error.id)),
    [errors, selectedIds],
  );

  const selectedByColumn = useMemo(() => {
    return {
      open: selectedErrors.filter((error) => {
        const column = getOperationalMeta(error).column;
        return column === 'needs_fix' || column === 'monitoring' || column === 'ready_to_close';
      }),
      ready: selectedErrors.filter((error) => getOperationalMeta(error).column === 'ready_to_close'),
      done: selectedErrors.filter((error) => getOperationalMeta(error).column === 'done'),
    };
  }, [selectedErrors]);

  const summary = useMemo(() => ({
    needsFix: grouped.needs_fix.length,
    monitoring: grouped.monitoring.length,
    ready: grouped.ready_to_close.length,
    done: grouped.done.length,
  }), [grouped]);

  const toggleSelection = (issueId: string, checked: boolean) => {
    setSelectedIds((current) => {
      if (checked) return Array.from(new Set([...current, issueId]));
      return current.filter((id) => id !== issueId);
    });
  };

  const runBulkAction = async (status: IssueActionStatus, issueIds: string[]) => {
    if (!onAction || issueIds.length === 0) return;
    await onAction({ issueIds, status, issueTitles: buildIssueTitles(issueIds, errors) });
    setSelectedIds([]);
  };

  if (isLoading) {
    return (
      <div className="grid gap-4 xl:grid-cols-4">
        {[1, 2, 3, 4].map((column) => (
          <Card key={column}>
            <CardHeader>
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-4 w-48" />
            </CardHeader>
            <CardContent className="space-y-3">
              {[1, 2, 3].map((item) => (
                <div key={item} className="rounded-lg border p-4">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="mt-2 h-3 w-1/2" />
                  <Skeleton className="mt-3 h-20 w-full" />
                </div>
              ))}
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (errors.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Bug className="h-5 w-5" />
            Painel de Issues
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border border-dashed bg-green-50 p-8 text-center">
            <p className="font-medium text-green-700">Nenhuma issue encontrada</p>
            <p className="mt-1 text-sm text-muted-foreground">Nao ha nada aberto nem historico no periodo selecionado.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const columns: KanbanColumnKey[] = ['needs_fix', 'monitoring', 'ready_to_close', 'done'];

  return (
    <>
      <Card className="border-dashed bg-muted/20">
        <CardContent className="flex flex-col gap-4 p-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="grid gap-3 md:grid-cols-4 lg:flex-1">
            <div className="rounded-lg border border-red-200 bg-red-50 p-3">
              <p className="text-xs font-medium text-red-700">Precisa corrigir</p>
              <p className="mt-1 text-2xl font-semibold text-red-700">{summary.needsFix}</p>
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs font-medium text-amber-700">Monitorando</p>
              <p className="mt-1 text-2xl font-semibold text-amber-700">{summary.monitoring}</p>
            </div>
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
              <p className="text-xs font-medium text-emerald-700">Prontas para fechar</p>
              <p className="mt-1 text-2xl font-semibold text-emerald-700">{summary.ready}</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <p className="text-xs font-medium text-slate-700">Fechadas / Ignoradas</p>
              <p className="mt-1 text-2xl font-semibold text-slate-700">{summary.done}</p>
            </div>
          </div>

          <div className="space-y-2 lg:w-[430px]">
            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" variant="outline" disabled={selectedByColumn.open.length === 0 || isUpdating} onClick={() => runBulkAction('marked_monitoring', selectedByColumn.open.map((error) => error.id))}>
                <Wrench className="mr-2 h-4 w-4" />
                Marcar corrigida
              </Button>
              <Button size="sm" disabled={selectedByColumn.ready.length === 0 || isUpdating} onClick={() => runBulkAction('resolved', selectedByColumn.ready.map((error) => error.id))}>
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Resolver no Sentry
              </Button>
              <Button size="sm" variant="outline" disabled={selectedByColumn.open.length === 0 || isUpdating} onClick={() => runBulkAction('ignored', selectedByColumn.open.map((error) => error.id))}>
                <Ban className="mr-2 h-4 w-4" />
                Ignorar
              </Button>
              <Button size="sm" variant="outline" disabled={selectedByColumn.done.length === 0 || isUpdating} onClick={() => runBulkAction('unresolved', selectedByColumn.done.map((error) => error.id))}>
                <RotateCcw className="mr-2 h-4 w-4" />
                Reabrir
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              {selectedIds.length} selecionada(s). A fila ativa mostra o que precisa de acao agora; a ultima coluna guarda o historico tratado.
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 xl:grid-cols-4">
        {columns.map((columnKey) => {
          const config = COLUMN_CONFIG[columnKey];
          const columnErrors = grouped[columnKey];

          return (
            <Card key={columnKey} className={cn(config.border, config.bg, 'min-h-[540px]')}>
              <CardHeader className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="text-base">{config.title}</CardTitle>
                  <Badge variant="secondary">{columnErrors.length}</Badge>
                </div>
                <p className="text-xs text-muted-foreground">{config.description}</p>
              </CardHeader>
              <CardContent className="space-y-3">
                {columnErrors.length === 0 ? (
                  <div className="rounded-lg border border-dashed bg-background/80 p-4 text-sm text-muted-foreground">
                    Nenhuma issue nesta coluna.
                  </div>
                ) : null}

                {columnErrors.map((error) => {
                  const meta = getOperationalMeta(error);
                  const canSelect = true;

                  return (
                    <div key={error.id} className="rounded-xl border bg-background p-4 shadow-sm">
                      <div className="flex items-start gap-3">
                        <Checkbox
                          checked={selectedIds.includes(error.id)}
                          onCheckedChange={(checked) => toggleSelection(error.id, checked === true)}
                          disabled={!canSelect || isUpdating}
                          className="mt-1"
                        />

                        <div className="mt-0.5">{getLevelIcon(error.level)}</div>

                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="line-clamp-2 text-sm font-medium">{error.title}</p>
                              <p className="mt-1 truncate font-mono text-xs text-muted-foreground">{error.culprit}</p>
                            </div>
                          </div>

                          <div className="mt-3 flex flex-wrap items-center gap-2">
                            {getLevelBadge(error.level)}
                            {getSentryStatusBadge(error)}
                            {meta.badge}
                          </div>

                          <div className="mt-3 grid gap-2 text-xs text-muted-foreground">
                            <div className="flex flex-wrap items-center gap-3">
                              <span className="flex items-center gap-1">
                                <Clock className="h-3.5 w-3.5" />
                                Ultimo evento {formatTimeAgo(error.lastSeen)}
                              </span>
                              <span>{error.count.toLocaleString('pt-BR')} ocorrencias</span>
                              <span>{error.userCount.toLocaleString('pt-BR')} usuarios</span>
                            </div>
                            {error.lastActionAt ? (
                              <span>
                                Ultima acao: {error.lastActionType} por {error.lastActionBy || 'Sistema'} {formatTimeAgo(error.lastActionAt)}
                              </span>
                            ) : (
                              <span>Nenhuma acao interna registrada ainda.</span>
                            )}
                          </div>

                          <div className="mt-3 rounded-lg bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
                            <span className="font-medium text-foreground">Proximo passo:</span> {meta.nextStep}
                          </div>

                          <div className="mt-4 flex flex-wrap gap-2">
                            {columnKey === 'needs_fix' || columnKey === 'monitoring' ? (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={isUpdating}
                                onClick={() => runBulkAction('marked_monitoring', [error.id])}
                              >
                                <Wrench className="mr-2 h-4 w-4" />
                                Marcar corrigida
                              </Button>
                            ) : null}

                            {columnKey === 'ready_to_close' ? (
                              <Button
                                size="sm"
                                disabled={isUpdating}
                                onClick={() => runBulkAction('resolved', [error.id])}
                              >
                                <CheckCircle2 className="mr-2 h-4 w-4" />
                                Resolver no Sentry
                              </Button>
                            ) : null}

                            {columnKey !== 'done' ? (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={isUpdating}
                                onClick={() => runBulkAction('ignored', [error.id])}
                              >
                                <Ban className="mr-2 h-4 w-4" />
                                Ignorar
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={isUpdating}
                                onClick={() => runBulkAction('unresolved', [error.id])}
                              >
                                <RotateCcw className="mr-2 h-4 w-4" />
                                Reabrir
                              </Button>
                            )}

                            <Button size="sm" variant="ghost" onClick={() => setHistoryIssue(error)}>
                              <Eye className="mr-2 h-4 w-4" />
                              Historico
                            </Button>

                            {error.permalink ? (
                              <Button size="sm" variant="ghost" asChild>
                                <a href={error.permalink} target="_blank" rel="noopener noreferrer">
                                  <ExternalLink className="mr-2 h-4 w-4" />
                                  Ver no Sentry
                                </a>
                              </Button>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <ErrorHistoryDialog error={historyIssue} open={!!historyIssue} onOpenChange={(open) => !open && setHistoryIssue(null)} />
    </>
  );
}
